import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import authService from './api-authorization/AuthorizeService';

interface RescheduleJobProps {
    scheduleId: number;
    publicHolidays: PublicHoliday[];
    shutdownPeriods: ShutdownPeriod[];
    reasonOptions: GenericList[];
    showModal: boolean;
    hideModal(): void;
    saveModal(scheduleDateNew: string): void;
}

interface PublicHoliday {
    name: string;
    date: string;
}

interface ShutdownPeriod {
    startDate: string;
    endDate: string;
}

interface GenericList {
    id: number;
    name: string;
}

//info about the scheduled job that isn't being updated on this screen
interface ScheduleDetail {
    scheduleId: number;
    scheduleDate: string;
    siteAddress: string;
    item: string;
    qtyType: string;
    typeId: number;
    scheduleBranchId: number;
}

const RescheduleJobData = (props: RescheduleJobProps) => {
    const [scheduleDetails, setScheduleDetails] = React.useState<ScheduleDetail>({
        scheduleId: 0,
        scheduleDate: "",
        siteAddress: "",
        item: "",
        qtyType: "",
        typeId: 1,              //Default to Install
        scheduleBranchId: -1,   //Default to Bayswater
    });
    const [newDate, setNewDate] = React.useState("");
    const [onOffHireDate, setOnOffHireDate] = React.useState("");
    const [rescheduleReasonId, setRescheduleReasonId] = React.useState(-1);
    const [reason, setReason] = React.useState("");
    const [showWarning, setShowWarning] = React.useState(false);
    const [dateLocked, setDateLocked] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        if (props.scheduleId > 0) {
            setLoading(true);
            getData();
        }
    }, [props.scheduleId]);
    

    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Schedule/GetRescheduleItemDetails?ScheduleId=' + props.scheduleId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setScheduleDetails(res.data.rescheduleItem);
            setOnOffHireDate(res.data.onOffHireDate);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const reset = () => {
        setNewDate("");
        setOnOffHireDate("");
        setRescheduleReasonId(-1);
        setErrors({});
        setLoading(false);
    }

    const hideModal = () => {
        reset();
        props.hideModal();
    }

    const resetErrors = (errorName: string) => {
        var errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handleReasonIdChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        resetErrors(e.target.name);
        setRescheduleReasonId(e.target.value * 1);
    }

    const handleReasonChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        resetErrors(e.target.name);
        setReason(e.target.value);
    }

    const handleOnOffhireChange = (e: any) => {
        resetErrors(e.target.name);
        setOnOffHireDate(e.target.value);
    }

    const handleNewDateChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        resetErrors(e.target.name);
        checkDateLocked(e.target.value);
    }

    const checkDateLocked = async (newDate: string) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        
        var showWarning = false;
        showWarning = checkHoliday(newDate);

        //check if date is locked
        axios.get('api/Schedule/CheckDateLocked?ScheduleDate=' + newDate + '&ScheduleBranchId=' + scheduleDetails.scheduleBranchId + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var locked = res.data;
            setNewDate(newDate);
            setShowWarning(showWarning);
            setDateLocked(locked);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const checkHoliday = (newDate: string) => {
        var isWarning = false;

        var schDate = new Date(newDate);
        schDate.setHours(0, 0, 0, 0);
        var isPublicHol = props.publicHolidays.filter(ph => {
            var phDate = new Date(ph.date);
            phDate.setHours(0, 0, 0, 0);
            if (phDate.getTime() == schDate.getTime()) return ph;
        }).length > 0;
        if (isPublicHol) {
            isWarning = true;
        } else {
            //check if it is within a shutdown period
            for (var i = 0; i < props.shutdownPeriods.length; i++) {
                var dateIsShutdown = DateHelpers.dateBetweenDates(newDate, props.shutdownPeriods[i].startDate, props.shutdownPeriods[i].endDate);
                if (dateIsShutdown) {
                    //if the date is in one shutdown period we don't need to check the rest
                    isWarning = true;
                    break;
                }
            }
        }

        return isWarning;
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (!newDate) {
            errorList["newDate"] = "Date is required";
            formIsValid = false;
        } else if (!DateHelpers.isValidDate(newDate)) {
            errorList["newDate"] = "Valid Date is required";
            formIsValid = false;
        }

        var type = "Onhire";
        if (scheduleDetails.typeId === 2) {
            type = "Offhire";
        }

        if (!onOffHireDate) {
            errorList["onOffHireDate"] = type + " Date is required";
            formIsValid = false;
        } else if (!DateHelpers.isValidDate(onOffHireDate)) {
            errorList["onOffHireDate"] = "Valid " + type + " Date is required";
            formIsValid = false;
        } else if (scheduleDetails.typeId === 1 && !DateHelpers.compareDate(newDate, onOffHireDate)) {
            errorList["onOffHireDate"] = type + " Date must be > New Date";
            formIsValid = false;
        } else if (scheduleDetails.typeId === 2 && !DateHelpers.compareDate(onOffHireDate, newDate)) {
            errorList["onOffHireDate"] = type + " Date must be < New Date";
            formIsValid = false;
        }

        if (props.reasonOptions.length > 0) {
            //when rescheduling an event job, we don't need to enter a reason as this will happen often
            if (rescheduleReasonId < 0) {
                errorList["rescheduleReasonId"] = " Reason is required";
                formIsValid = false;
            }

            if (rescheduleReasonId === 3) {
                if (!reason) {
                    errorList["reason"] = " Reason Description is required";
                    formIsValid = false;
                }
            }
        }

        setErrors(errorList);
        return formIsValid;
    }

    const reschedule = (e: any) => {
        e.preventDefault();
        if (validate()) {
            saveReschedule();
        }
    }

    const saveReschedule = async() => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var updatedDate = newDate;

        var reschedulejob = {
            scheduleId: scheduleDetails.scheduleId,
            newScheduleDate: newDate,
            onOffHireDate: onOffHireDate,
            item: scheduleDetails.item,
            rescheduleReasonId: rescheduleReasonId,
            reason: reason,
            subId: user.sub
        };

        axios.post('api/Schedule/SaveRescheduleJob', reschedulejob, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Job Rescheduled Successfully");

                reset();
                props.saveModal(updatedDate);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    let type = "Onhire";
    if (scheduleDetails.typeId === 2) {
        type = "Offhire";
    }

    const renderDetails = (
        <div className="static-modal">
            <Modal show={props.showModal} onHide={hideModal}>
                <Modal.Header className="modal__header--colour">
                    <Modal.Title>Reschedule - {scheduleDetails.item} {scheduleDetails.qtyType}</Modal.Title>
                    <button type="button" className="close" onClick={hideModal}>&times;</button>
                </Modal.Header>
                <Modal.Body>
                    <div>{scheduleDetails.siteAddress}</div>
                    <label className="input-group" htmlFor="scheduleDate">
                        <span className="label">Existing Date </span>
                        <input className="input" type="string" id="scheduleDate" name="scheduleDate" value={scheduleDetails.scheduleDate} disabled></input>
                    </label>
                    <label className="input-group" htmlFor="newDate">
                        <span className="label">New Date </span>
                        <input className="input" type="date" id="newDate" name="newDate" value={newDate} onChange={(e) => handleNewDateChange(e)}></input>
                    </label>
                    <span className="label errors">{errors["newDate"]}</span>

                    <label className="input-group" htmlFor="onOffHireDate">
                        <span className="label">{type} Date </span>
                        <input className="input" type="date" id="onOffHireDate" name="onOffHireDate" value={onOffHireDate} onChange={(e) => handleOnOffhireChange(e)}></input>
                    </label>
                    <span className="label errors">{errors["onOffHireDate"]}</span>

                    <label className={props.reasonOptions.length > 0 ? "input-group" : "hidden"} htmlFor="rescheduleReasonId">
                        <span className="label">Reason</span>
                        <select className="select rescheduleReason--width" id="rescheduleReasonId" name="rescheduleReasonId" value={rescheduleReasonId} onChange={(e) => handleReasonIdChange(e)}>
                            <option hidden defaultValue="-1"></option>
                            {props.reasonOptions.map(reason =>
                                <option key={reason.id} value={reason.id}>{reason.name}</option>
                            )};
                        </select>
                    </label>
                    <span className="label errors">{errors["rescheduleReasonId"]}</span>

                    <div className={rescheduleReasonId === 3 ? "" : "hidden"}>
                        <label className="input-group" htmlFor="reason">
                            <span className="label">Other Reason</span>
                            <textarea className="input job__noteSize" id="reason" name="reason" value={reason} onChange={(e) => handleReasonChange(e)}></textarea>
                        </label>
                        <span className="label errors">{errors["reason"]}</span>
                    </div>

                    <span className={showWarning ? "makeitred" : "hidden"}>Warning: the date selected is a Public Holiday or within a Shutdown Period!</span>
                    <span className={dateLocked ? "makeitred" : "hidden"}>The date selected has been locked! You will need to unlock to schedule more jobs.</span>
                </Modal.Body>
                <Modal.Footer>
                    <button className="popup-button--yes" onClick={reschedule} disabled={dateLocked}>Reschedule</button>
                </Modal.Footer>
            </Modal>
        </div>
    )

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (<div>
        {contents}
    </div>);
}

export default RescheduleJobData;